import app from 'app';
import _ from '#/lodash-extended';
import { workPool } from '@wochitnpm/helpers';

/**
 * @namespace workPool.job.transcriptions
 */
export default workPool.define(
  'transcriptions',
  {
    initialize() {
      this.isListening = false;
      const { assetUUID } = this.options.asset.metadata;
      this.options.assetUUID = assetUUID;

      this.onFinish(() => {
        if (this.isListening) {
          app.api.realTime.cancelListenOnTranscription(assetUUID);
        }
      });
    },

    getContext() {
      const { assetUUID, storyId } = this.options;
      return {
        assetUUID,
        storyId,
      };
    },

    run(res) {
      const { assetUUID, storyId, how, languageCode } = this.options;

      if (this.constructor.HOW.CREATE === how) {
        // when adding new asset, POST to create transcription immediately
        this._createTranscription();
      } else if (this.constructor.HOW.READ === how) {
        // when entering a story, check if transcription already exists.
        app.api.Transcriptions.GET({ assetUUID, storyId, languageCode })
          .then((res) => {
            return this.onDone(res);
          })
          .catch((res) => {
            if (
              res?.originalTranscription?.transcriptionStatus ===
                'PROCESSING' ||
              res?.status === 'PROCESSING'
            ) {
              this._listenTranscription();
            } else {
              this.onFailToGet();
            }
          });
      } else if (this.constructor.HOW.READ_OR_CREATE === how) {
        if (languageCode) {
          app.api.Transcriptions.GET_TRANSLATION({
            assetUUID,
            languageCode,
          })
            .then((res) => {
              return this.onDone(res);
            })
            .catch((res) => {
              if (!res || res.error) {
                this._createTranslation();
              } else {
                this.onFailToGet();
              }
            });
        } else {
          app.api.Transcriptions.GET({ assetUUID, storyId, languageCode })
            .then((res) => {
              return this.onDone(res);
            })
            .catch((res) => {
              if (!res || res.error) {
                this._createTranscription();
              } else if (
                res?.originalTranscription?.transcriptionStatus ===
                  'PROCESSING' ||
                res?.status === 'PROCESSING'
              ) {
                this._listenTranscription();
              } else {
                this.onFailToGet();
              }
            });
        }
      } else {
        app.doctorController.report(
          `don't know how to '${how}' @ workPool.job.transcriptions`
        );
      }
    },

    _createTranscription() {
      const {
        assetUUID,
        storyId,
        transcribeSubType,
        programmerAllowedTranscription,
        languageCode,
      } = this.options;

      if (
        app.transcribeSubType.NARRATION === transcribeSubType &&
        !programmerAllowedTranscription
      ) {
        return this.onIrrelevantCreate();
      }

      this.options.startTime = performance.now();
      app.api.Transcriptions.POST({
        assetUUID,
        storyId,
        transcribeSubType,
        languageCode,
      })
        .then((status) => {
          if (status === 'DONE') {
            app.api.Transcriptions.GET({
              assetUUID,
              storyId,
              languageCode,
            })
              .then((res) => {
                return this.onDone(res);
              })
              .catch(() => {
                return this.onFailToCreate();
              });
          } else {
            this._listenTranscription();
          }
        })
        .catch(() => this.onFailToCreate());
    },

    _createTranslation() {
      const {
        assetUUID,
        storyId,
        transcribeSubType,
        programmerAllowedTranscription,
        languageCode,
      } = this.options;

      if (
        app.transcribeSubType.NARRATION === transcribeSubType &&
        !programmerAllowedTranscription
      ) {
        return this.onIrrelevantCreate();
      }

      this.options.startTime = performance.now();
      app.api.Transcriptions.POST_TRANSLATION({
        assetUUID,
        storyId,
        transcribeSubType,
        languageCode,
      })
        .then(() => {
          app.api.Transcriptions.GET_TRANSLATION({
            assetUUID,
            storyId,
            languageCode,
          })
            .then((res) => {
              return this.onDone(res);
            })
            .catch((res) => {
              this.onFailToGet();
            });
        })
        .catch(() => this.onFailToCreate());
    },

    _listenTranscription() {
      const { assetUUID, storyId, languageCode } = this.options;
      const { fileSize, duration } = this.options.asset.metadata;
      let timeoutMin = 5; // (minutes)

      if (duration) {
        timeoutMin = Math.max(timeoutMin, ((duration / 2) * 3) / 60);
      } else if (fileSize) {
        timeoutMin = fileSize < 1e9 ? 10 : fileSize < 2e9 ? 20 : 30;
      }

      this.isListening = true;
      this.$emit({ processing: true });

      app.api.realTime
        .listenOnTranscription({ assetUUID, timeoutMin })
        .then(() => {
          this.isListening = false;
          return app.api.Transcriptions.GET({
            assetUUID,
            storyId,
            languageCode,
          });
        })
        .then((res) => {
          return this.onDone(res);
        })
        .catch(() => {
          return this.onFailToCreate();
        })
        .then(() => {
          app.api.realTime.cancelListenOnTranscription(assetUUID);
        });
    },

    onDone(response) {
      const { assetUUID, startTime } = this.options;

      this.$emit(response);
      this.finish();

      if (startTime) {
        const timeDiff = app.moment
          .utc(performance.now() - startTime)
          .format('m:ss');
        console.log(
          'Transcription took',
          timeDiff,
          'minutes, asset:',
          assetUUID
        );
      }
    },

    onIrrelevantCreate() {
      this.$emit({ error: false, irrelevant: true, tokens: [] });
      this.abort();
    },

    onFailToGet() {
      this.$emit({ error: true, irrelevant: false });
      this.abort();
    },

    onFailToCreate() {
      this.$emit({ error: true, failedToCreate: true });
      this.abort();
    },
  },
  {
    HOW: {
      READ: 'READ',
      CREATE: 'CREATE',
      READ_OR_CREATE: 'READ_OR_CREATE',
    },

    start({ baseObj, field, timeout, interval }) {
      workPool.add(new this({ baseObj, field, timeout, interval }));
    },

    isRunning(asset) {
      const assetUUID = asset.metadata.assetUUID;
      return workPool.has({
        type: this.type,
        context: (jc) => jc.assetUUID === assetUUID,
      });
    },

    execute({
      asset,
      storyId,
      transcribeSubType,
      how,
      programmerAllowedTranscription,
      alertOnError,
      languageCode,
    }) {
      if (!this.isRunning(asset)) {
        workPool.add(
          new this({
            asset,
            storyId,
            transcribeSubType,
            how,
            programmerAllowedTranscription,
            alertOnError,
            languageCode,
          })
        );
      }
    },

    parseTranscribeResult({ assetUUID, res }) {
      let rv;

      if (res.processing) {
        rv = {
          assetUUID: assetUUID,
          value: {
            status: 'PROCESSING',
          },
        };
      } else if (res.irrelevant) {
        rv = {
          assetUUID: assetUUID,
          value: {
            status: 'DONE',
            isOriginal: true,
            tokens: [],
            originalTokens: [],
            shouldPostNew: true,
          },
        };
      } else {
        rv = {
          assetUUID: assetUUID,
          value: {
            status: res.error ? 'FAILED' : 'DONE',
            isOriginal: res.error ? null : _.isEmpty(res.tokens), // no tokens = no update.
            tokens: res.error
              ? null
              : res.tokens.length
              ? res.tokens
              : res.originalTranscription?.tokens || res.tokens,
            originalTokens: res.error
              ? null
              : res.originalTranscription?.tokens || res.tokens,
            languageCode: res.originalTranscription?.languageCode ?? 'unknown',
          },
        };
      }

      return rv;
    },

    setDefaultCC({ assetUUID, visualData, hisTranscript, enableVOSubtitles }) {
      const visualTracks = _.filter(
        visualData,
        (a) => a.metadata.assetUUID === assetUUID
      );

      if (!_.isEmpty(visualTracks) && !_.isEmpty(hisTranscript)) {
        let changed = false;
        _.each(visualTracks, (vt) => {
          const hasDefaultValue = null === vt.agd.showTranscription;
          const hasTokens = !_.isEmpty(hisTranscript.tokens);
          if (hasTokens && hasDefaultValue) {
            vt.agd.showTranscription = enableVOSubtitles;
            changed = true;
          }
        });

        if (changed) {
          app.timelineGateway.serializeTimeline({
            e: 'reflexive change of visual showTranscription',
            skipHistory: false,
          });
        }
      }
    },
  }
);
